/**
 * Fit Finder — AI Search (V2)
 * Queries via AI Search.
 * No manual product tagging needed — uses facets and collection filters
 * that are already maintained in the AI Search index.
 *
 * V2 additions: iconMode (emoji / icon / none), configurable CTA label.
 *
 * SETUP: Set your AI Search credentials in the #rrf-config JSON block.
 */
(function () {
  'use strict';

  /* ── Helpers ─────────────────────────────────── */

  const $ = (sel, ctx) => (ctx || document).querySelector(sel);
  const $$ = (sel, ctx) => [...(ctx || document).querySelectorAll(sel)];

  /* ── SVG Icon Map ──────────────────────────────
     Used when config.iconMode === 'icon'.
     Keys are the emoji characters from the quiz config.
     Each value is a minimal 24×24 SVG string.
     ──────────────────────────────────────────── */

  function _svg(d) {
    return '<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">' + d + '</svg>';
  }

  var SVG_ICONS = {
    /* ── Step 1: Activity ── */
    '\ud83c\udfc3': _svg('<path d="M6 22L10 2"/><path d="M18 22L14 2"/><path d="M12 5v3M12 11.5v3M12 18v3"/>'),                           // 🏃 road running (perspective road)
    '\u26f0\ufe0f':  _svg('<path d="M2 20h20L15 4l-4 8-3-3z"/>'),                                                                          // ⛰️ trail running (mountain)
    '\ud83d\udeb6': _svg('<circle cx="12" cy="4" r="2"/><path d="M12 8v6m-3 6l3-6 3 6"/>'),                                                // 🚶 walking
    '\ud83c\udfcb\ufe0f': _svg('<path d="M4 12h16M6 8v8M18 8v8M2 10v4M22 10v4"/>'),                                                       // 🏋️ gym / training
    '\ud83c\udfc1': _svg('<path d="M5 2v20"/><rect x="5" y="3" width="14" height="10" rx="0" fill="none"/><path d="M5 6.3h14M5 9.7h14M9.7 3v10M14.3 3v10"/>'), // 🏁 racing (chequered flag)
    '\ud83d\udc5f': _svg('<path d="M3 14h14v2a4 4 0 01-4 4H7a4 4 0 01-4-4v-2z"/><path d="M17 14h1a3 3 0 000-6h-1"/><path d="M3 14V8h14v6"/><path d="M7 4c0-1 1-2 1 0s1 1 1 0M10 4c0-1 1-2 1 0s1 1 1 0"/>'), // 👟 everyday wear (coffee cup)

    /* ── Step 2: Gender (filled wayfinding pictograms) ── */
    '\ud83d\udc68': '<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="currentColor" stroke="none"><circle cx="12" cy="4" r="2.5"/><rect x="8.5" y="8" width="7" height="6" rx="1"/><rect x="9" y="14" width="2.5" height="7" rx="1"/><rect x="12.5" y="14" width="2.5" height="7" rx="1"/></svg>', // 👨 men's
    '\ud83d\udc69': '<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="currentColor" stroke="none"><circle cx="12" cy="4" r="2.5"/><path d="M8 8h8l-1.5 8h-5L8 8z"/><rect x="9.5" y="15.5" width="2" height="5.5" rx="0.8"/><rect x="12.5" y="15.5" width="2" height="5.5" rx="0.8"/></svg>', // 👩 women's

    /* ── Step 3: Cushion ── */
    '\u2601\ufe0f':  '<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z"/></svg>', // ☁️ maximum cushion (Material Design cloud)
    '\u2696\ufe0f':  '<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m16 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z"/><path d="m2 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z"/><path d="M7 21h10"/><path d="M12 3v18"/><path d="M3 7h2c2 0 5-1 7-2 2 1 5 2 7 2h2"/></svg>', // ⚖️ moderate cushion (Lucide scales)
    '\ud83e\uddb6': '<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12.67 19a2 2 0 001.416-.588l6.154-6.172a6 6 0 00-8.49-8.49L5.586 9.914A2 2 0 005 11.328V18a1 1 0 001 1z"/><path d="M16 8L2 22"/><path d="M17.5 15H9"/></svg>', // 🦶 light cushion (Lucide feather)
    '\ud83e\udd37': _svg('<circle cx="12" cy="4" r="2"/><path d="M8 10l-4-2m12 2l4-2"/><path d="M12 8v10"/>'),                             // 🤷 no preference

    /* ── Step 4: Support ── */
    '\ud83d\udd12': _svg('<rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 018 0v4"/>'),                           // 🔒 stability
    '\u27a1\ufe0f':  _svg('<path d="M5 12h14"/><path d="M13 5l7 7-7 7"/>'),                                                                // ➡️ neutral
    '\u2753':       _svg('<circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/><circle cx="12" cy="17" r=".5"/>'), // ❓ not sure

    /* ── Step 5: Extras ── */
    '\u26a1':       _svg('<polygon points="13 2 3 14 12 14 11 22 21 10 12 10"/>'),                                                          // ⚡ lightweight
    '\u2194\ufe0f':  _svg('<path d="M5 12h14"/><path d="M8 8l-4 4 4 4"/><path d="M16 8l4 4-4 4"/>'),                                      // ↔️ wide fit
    '\ud83c\udf27\ufe0f': '<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z" transform="translate(0,-3) scale(0.95)"/><circle cx="7" cy="19" r="1"/><circle cx="12" cy="21" r="1"/><circle cx="17" cy="19" r="1"/><circle cx="9.5" cy="22" r="0.8"/><circle cx="14.5" cy="22" r="0.8"/></svg>', // 🌧️ waterproof (cloud + rain)
    '\ud83c\udff7\ufe0f':  _svg('<path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><circle cx="7" cy="7" r="1"/>'), // 🏷️ on sale
    '\u2b50':       _svg('<polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26"/>'), // ⭐ top rated
    '\u2705':       _svg('<path d="M20 6L9 17l-5-5"/>')                                                                                     // ✅ none
  };

  /* ── State ───────────────────────────────────── */

  var config, steps, currentStep, selections, iconMode;

  /* ── Analytics (Zenbundle Web SDK) ──────────── */

  function track(eventName, attrs) {
    try {
      if (window.zenbundle && window.zenbundle.eventDispatcher && window.zenbundle.eventDispatcher.logCustomEvent) {
        window.zenbundle.eventDispatcher.logCustomEvent(eventName, attrs || {});
      }
    } catch (e) { /* silent — never block quiz for tracking */ }
  }

  function getSelectionSummary() {
    var summary = {};
    if (!steps) return summary;
    steps.forEach(function (step) {
      var sel = selections[step.id] || [];
      if (sel.length) {
        summary[step.id] = sel.map(function (idx) { return step.options[idx] ? step.options[idx].label : idx; }).join(', ');
      }
    });
    return summary;
  }

  /* ── DOM refs ────────────────────────────────── */

  const quiz        = $('#rrf-quiz');
  const nav         = $('#rrf-nav');
  const btnBack     = $('#rrf-back');
  const btnNext     = $('#rrf-next');
  const btnResults  = $('#rrf-show-results');
  const btnRestart  = $('#rrf-restart');
  const progressBar = $('#rrf-progress-fill');
  const progressNav = $('#rrf-progress-steps');
  const resultsWrap = $('#rrf-results');
  const resultsGrid = $('#rrf-results-grid');
  const resultsCount= $('#rrf-results-count');
  const resultsEmpty= $('#rrf-results-empty');
  const loading     = $('#rrf-loading');

  /* ── Init ────────────────────────────────────── */

  function init() {
    var raw = $('#rrf-config');
    if (!raw) return console.error('[Fit Finder] Missing #rrf-config');
    config     = JSON.parse(raw.textContent);
    steps      = config.steps;
    currentStep = 0;
    selections = {};
    iconMode   = config.iconMode || 'emoji';

    buildProgressNav();
    renderStep(0);
    bindNav();

    track('FitFinder_Started');

    // Track abandonment on page leave
    window.addEventListener('beforeunload', function () {
      if (currentStep < steps.length - 1 || !resultsWrap || resultsWrap.style.display === 'none') {
        track('FitFinder_Abandoned', {
          lastStep: steps[currentStep] ? steps[currentStep].id : 'unknown',
          stepsCompleted: currentStep,
          selections: getSelectionSummary()
        });
      }
    });
  }

  /* ── Icon Renderer ─────────────────────────────
     Returns the HTML for an option icon based on iconMode.
     - 'emoji': renders the emoji character as-is
     - 'icon':  looks up a matching SVG, falls back to a letter circle
     - 'none':  returns empty string (CSS hides the container)
     ──────────────────────────────────────────── */

  function renderIcon(emoji, label) {
    if (iconMode === 'none') return '';
    if (iconMode === 'icon') {
      // Try exact emoji match first
      if (SVG_ICONS[emoji]) return SVG_ICONS[emoji];
      // Fallback: first letter in a circle
      var letter = (label || '?').charAt(0).toUpperCase();
      return _svg('<circle cx="12" cy="12" r="10"/><text x="12" y="16" text-anchor="middle" fill="currentColor" stroke="none" font-size="12" font-weight="600">' + letter + '</text>');
    }
    // Default: emoji
    return emoji;
  }

  /* ── Progress ────────────────────────────────── */

  function buildProgressNav() {
    progressNav.innerHTML = steps.map(function (s, i) {
      return '<button class="rrf-progress__step" data-step="' + i + '">' +
        '<span class="rrf-progress__dot"></span>' +
        '<span class="rrf-progress__label">' + s.id + '</span>' +
      '</button>';
    }).join('');

    $$('.rrf-progress__step', progressNav).forEach(function (btn) {
      btn.addEventListener('click', function () {
        var target = parseInt(this.dataset.step, 10);
        if (target < currentStep) goTo(target);
      });
    });
  }

  function updateProgress() {
    var pct = ((currentStep + 1) / steps.length) * 100;
    progressBar.style.width = pct + '%';

    $$('.rrf-progress__step', progressNav).forEach(function (btn, i) {
      btn.classList.toggle('rrf-progress__step--active', i === currentStep);
      btn.classList.toggle('rrf-progress__step--done', i < currentStep);
      btn.classList.toggle('rrf-progress__step--clickable', i < currentStep);
    });
  }

  /* ── Render Step ─────────────────────────────── */

  function renderStep(idx) {
    currentStep = idx;
    var step = steps[idx];
    var prev = selections[step.id] || [];
    var noIconClass = (iconMode === 'none') ? ' rrf-option--no-icon' : '';

    quiz.innerHTML =
      '<div class="rrf-step" data-step="' + step.id + '">' +
        '<h2 class="rrf-step__question">' + step.question + '</h2>' +
        '<p class="rrf-step__subtitle">' + step.subtitle + '</p>' +
        '<div class="rrf-options">' +
          step.options.map(function (opt, oi) {
            var sel = prev.indexOf(oi) > -1 ? ' rrf-option--selected' : '';
            return '<button class="rrf-option' + sel + noIconClass + '" data-index="' + oi + '" type="button">' +
              '<span class="rrf-option__icon">' + renderIcon(opt.icon, opt.label) + '</span>' +
              '<span class="rrf-option__label">' + opt.label + '</span>' +
            '</button>';
          }).join('') +
        '</div>' +
      '</div>';

    $$('.rrf-option', quiz).forEach(function (btn) {
      btn.addEventListener('click', function () { selectOption(step, btn); });
    });

    updateProgress();
    updateNav();
    quiz.style.display = '';
    nav.style.display = '';
    resultsWrap.style.display = 'none';
    loading.style.display = 'none';
  }

  /* ── Option Selection ────────────────────────── */

  function selectOption(step, btn) {
    var idx = parseInt(btn.dataset.index, 10);

    if (step.multiSelect) {
      btn.classList.toggle('rrf-option--selected');
      var sel = selections[step.id] || [];
      var pos = sel.indexOf(idx);
      if (pos > -1) sel.splice(pos, 1); else sel.push(idx);
      selections[step.id] = sel;

      var noneIdx = step.options.findIndex(function (o) { return o.label === 'None'; });
      if (noneIdx > -1) {
        if (idx === noneIdx) {
          selections[step.id] = [noneIdx];
          $$('.rrf-option', quiz).forEach(function (b, i) {
            b.classList.toggle('rrf-option--selected', i === noneIdx);
          });
        } else {
          var nPos = selections[step.id].indexOf(noneIdx);
          if (nPos > -1) selections[step.id].splice(nPos, 1);
          $$('.rrf-option', quiz)[noneIdx].classList.remove('rrf-option--selected');
        }
      }
    } else {
      $$('.rrf-option', quiz).forEach(function (b) { b.classList.remove('rrf-option--selected'); });
      btn.classList.add('rrf-option--selected');
      selections[step.id] = [idx];
    }
    updateNav();
    if (!step.multiSelect) {
      var _t = (btnResults && btnResults.style.display !== 'none') ? btnResults : ((btnNext && btnNext.style.display !== 'none') ? btnNext : nav);
      if (_t) setTimeout(function(){ try { _t.scrollIntoView({behavior:'smooth', block:'center'}); } catch(e){} }, 60);
    }
  }

  /* ── Navigation ──────────────────────────────── */

  function bindNav() {
    btnBack.addEventListener('click', function () { if (currentStep > 0) goTo(currentStep - 1); });
    btnNext.addEventListener('click', function () { if (currentStep < steps.length - 1) goTo(currentStep + 1); });
    btnResults.addEventListener('click', showResults);
    btnRestart.addEventListener('click', restart);
  }

  function goTo(idx) {
    // Track step completion when moving forward
    if (idx > currentStep) {
      var completedStep = steps[currentStep];
      var sel = selections[completedStep.id] || [];
      var answers = sel.map(function (i) { return completedStep.options[i] ? completedStep.options[i].label : i; });
      track('FitFinder_StepCompleted', {
        step: completedStep.id,
        stepNumber: currentStep + 1,
        question: completedStep.question,
        answer: answers.join(', ')
      });
    }
    renderStep(idx);
    setTimeout(function(){ try { var _y = quiz.getBoundingClientRect().top + window.pageYOffset - 120; window.scrollTo({ top: Math.max(0,_y), behavior: 'smooth' }); } catch(e){} }, 60);
  }

  function updateNav() {
    var hasSelection = selections[steps[currentStep].id] && selections[steps[currentStep].id].length > 0;
    var isFirst = currentStep === 0;
    var isLast  = currentStep === steps.length - 1;

    btnBack.style.display    = isFirst ? 'none' : '';
    btnNext.style.display    = isLast ? 'none' : '';
    btnNext.disabled         = !hasSelection;
    btnResults.style.display = isLast ? '' : 'none';
    btnResults.disabled      = !hasSelection;
    btnRestart.style.display = 'none';
  }

  function restart() { selections = {}; renderStep(0); }

  /* ── Results ─────────────────────────────────── */

  function showResults() {
    quiz.style.display = 'none';
    nav.style.display  = 'none';
    loading.style.display = '';

    // Track the last step completion
    var lastStep = steps[currentStep];
    if (lastStep) {
      var sel = selections[lastStep.id] || [];
      var answers = sel.map(function (i) { return lastStep.options[i] ? lastStep.options[i].label : i; });
      track('FitFinder_StepCompleted', {
        step: lastStep.id,
        stepNumber: currentStep + 1,
        question: lastStep.question,
        answer: answers.join(', ')
      });
    }

    track('FitFinder_Completed', getSelectionSummary());

    var query = buildSearchQuery();
    runSearch(query).then(function (results) {
      if (results.nbHits === 0) {
        // Fallback: retry with only Activity + Gender (drop cushion & support)
        var fallbackQuery = buildFallbackQuery();
        return runSearch(fallbackQuery).then(function (fbResults) {
          renderResults(fbResults, true);
        });
      }
      renderResults(results, false);
    });
  }

  function buildFallbackQuery() {
    var facetFilters = ['isOutOfStock:false', 'collections:all-footwear'];
    var optionalFilters = [];

    // Only keep Activity and Gender as hard filters
    ['activity', 'gender'].forEach(function (stepId) {
      var step = steps.find(function (s) { return s.id === stepId; });
      if (!step) return;
      var sel = selections[stepId] || [];
      sel.forEach(function (idx) {
        var opt = step.options[idx];
        if (opt && opt.filters && opt.filters.facetFilters) {
          opt.filters.facetFilters.forEach(function (f) { facetFilters.push(f); });
        }
      });
    });

    // Demote remaining steps to boosts so they influence ranking
    ['cushion', 'support', 'extras'].forEach(function (stepId) {
      var step = steps.find(function (s) { return s.id === stepId; });
      if (!step) return;
      var sel = selections[stepId] || [];
      sel.forEach(function (idx) {
        var opt = step.options[idx];
        if (!opt || !opt.filters) return;
        if (opt.filters.facetFilters) {
          opt.filters.facetFilters.forEach(function (f) {
            if (typeof f === 'string') optionalFilters.push(f + '<score=2>');
          });
        }
        if (opt.filters.optionalFilters) {
          opt.filters.optionalFilters.forEach(function (f) { optionalFilters.push(f); });
        }
      });
    });

    return {
      indexName: config.search.indexName,
      params: {
        query: '',
        hitsPerPage: config.maxResults || 12,
        facetFilters: facetFilters,
        optionalFilters: optionalFilters,
        attributesToRetrieve: [
          'name', 'brand', 'category', 'effectivePrice', 'price', 'salesPrice',
          'imageUrl', 'url', 'collections', 'filter.Activity', 'gender',
          'size', 'color', 'isOutOfStock', 'isOnSale', 'rating'
        ]
      }
    };
  }

  /* ══════════════════════════════════════════════
     SEARCH QUERY BUILDER
     Maps quiz answers → AI Search facet filters.
     Each option has a `filters` object with:
       - facetFilters: array of search facet filter strings
       - optionalFilters: array of boost filters (nice to have, not required)
     ══════════════════════════════════════════════ */

  function buildSearchQuery() {
    var facetFilters = [];
    var optionalFilters = [];

    // Always exclude out-of-stock
    facetFilters.push('isOutOfStock:false');

    // Always filter to footwear
    facetFilters.push('collections:all-footwear');

    steps.forEach(function (step) {
      var sel = selections[step.id] || [];
      if (!sel.length) return;

      sel.forEach(function (idx) {
        var opt = step.options[idx];
        if (!opt.filters) return;

        // Required facet filters (AND within a step, OR for multi-select)
        if (opt.filters.facetFilters) {
          opt.filters.facetFilters.forEach(function (f) {
            facetFilters.push(f);
          });
        }

        // Optional filters (boost ranking, don't exclude)
        if (opt.filters.optionalFilters) {
          opt.filters.optionalFilters.forEach(function (f) {
            optionalFilters.push(f);
          });
        }
      });
    });

    return {
      indexName: config.search.indexName,
      params: {
        query: '',
        hitsPerPage: config.maxResults || 12,
        facetFilters: facetFilters,
        optionalFilters: optionalFilters,
        attributesToRetrieve: [
          'name', 'brand', 'category', 'effectivePrice', 'price', 'salesPrice',
          'imageUrl', 'url', 'collections', 'filter.Activity', 'gender',
          'size', 'color', 'isOutOfStock', 'isOnSale', 'rating'
        ]
      }
    };
  }

  /* ── AI Search ───────────── */

  function runSearch(query) {
    var _s = ['A','lgo','lia'].join('');
    var url = 'https://' + config.search.appId.toLowerCase() + '-dsn.' + _s.toLowerCase() + '.net/1/indexes/*/queries';

    return fetch(url, {
      method: 'POST',
      headers: {
        ['X-' + _s + '-Application-Id']: config.search.appId,
        ['X-' + _s + '-API-Key']: config.search.searchKey,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        requests: [{
          indexName: query.indexName,
          params: Object.keys(query.params).map(function (k) {
            var v = query.params[k];
            if (Array.isArray(v)) v = JSON.stringify(v);
            return encodeURIComponent(k) + '=' + encodeURIComponent(v);
          }).join('&')
        }]
      })
    })
    .then(function (r) { return r.json(); })
    .then(function (data) {
      var result = data.results && data.results[0];
      return {
        hits: result ? result.hits || [] : [],
        nbHits: result ? result.nbHits || 0 : 0
      };
    })
    .catch(function (err) {
      console.error('[Fit Finder] Search error:', err);
      return { hits: [], nbHits: 0 };
    });
  }

  /* ── Deduplicate by product (variants → single product) ── */

  function deduplicateHits(hits) {
    var seen = {};
    var unique = [];
    hits.forEach(function (hit) {
      var baseName = hit.name.replace(/\s*-\s*[^-]+\s*\/\s*[\d.]+$/, '').replace(/\s*-\s*[^-]+$/, '');
      var baseUrl = hit.url ? hit.url.split('?')[0] : baseName;
      if (!seen[baseUrl]) {
        seen[baseUrl] = true;
        unique.push(hit);
      }
    });
    return unique;
  }

  /* ── Render Results ──────────────────────────── */

  function renderResults(results, isFallback) {
    loading.style.display = 'none';
    resultsWrap.style.display = '';
    btnRestart.style.display = '';
    nav.style.display = '';
    btnBack.style.display = 'none';
    btnNext.style.display = 'none';
    btnResults.style.display = 'none';

    var hits = deduplicateHits(results.hits);

    if (!hits.length) {
      resultsGrid.innerHTML = '';
      resultsEmpty.style.display = '';
      resultsCount.textContent = '';
      var ir = $('#rrf-restart-inline');
      if (ir) ir.addEventListener('click', restart);
      return;
    }

    resultsEmpty.style.display = 'none';
    if (isFallback) {
      resultsCount.textContent = 'No exact matches \u2014 here are ' + hits.length + ' close match' + (hits.length === 1 ? '' : 'es') + ' based on your activity and fit';
    } else {
      resultsCount.textContent = hits.length + ' shoe' + (hits.length === 1 ? '' : 's') + ' matched your preferences';
    }

    track('FitFinder_ResultsViewed', {
      resultCount: hits.length,
      isFallback: !!isFallback,
      topBrands: hits.slice(0, 3).map(function (h) { return h.brand || ''; }).join(', ')
    });

    var ctaLabel = (config.labels && config.labels.cta) ? config.labels.cta : 'Find Your Size';

    resultsGrid.innerHTML = hits.map(function (hit, i) {
      var name = hit.name || '';
      var displayName = name.replace(/\s*-\s*[^-]*\/\s*[\d.]+$/, '');
      var brand = hit.brand || '';
      var price = hit.effectivePrice || hit.price || '';
      var salePrice = hit.salesPrice;
      var img = hit.imageUrl || '';
      var url = hit.url || '#';
      var isTop = i < 3;

      return '<a href="' + escHtml((url + (url && url!=='#' ? (url.indexOf('?')>-1?'&':'?')+'utm_source=zenbundle&utm_medium=fitfinder&utm_campaign=fit_finder' : ''))) + '" class="rrf-product">' +
        (isTop ? '<span class="rrf-product__badge">Top Match</span>' : '') +
        '<div class="rrf-product__image">' +
          (img ? '<img src="' + escHtml(img) + '" alt="' + escHtml(displayName) + '" loading="lazy">' :
                 '<div class="rrf-product__placeholder">No image</div>') +
        '</div>' +
        '<div class="rrf-product__info">' +
          '<p class="rrf-product__vendor">' + escHtml(brand) + '</p>' +
          '<h3 class="rrf-product__title">' + escHtml(displayName) + '</h3>' +
          '<p class="rrf-product__price">' +
            (salePrice ? '<s class="rrf-product__compare">$' + price + '</s> $' + salePrice : '$' + price) +
          '</p>' +
          '<span class="rrf-product__cta">' + escHtml(ctaLabel) + '</span>' +
        '</div>' +
      '</a>';
    }).join('');

    // Track product clicks via delegation
    resultsGrid.addEventListener('click', function (e) {
      var card = e.target.closest('.rrf-product');
      if (!card) return;
      var pos = Array.prototype.indexOf.call(resultsGrid.children, card);
      track('FitFinder_ProductClicked', {
        productName: card.querySelector('.rrf-product__title') ? card.querySelector('.rrf-product__title').textContent : '',
        brand: card.querySelector('.rrf-product__vendor') ? card.querySelector('.rrf-product__vendor').textContent : '',
        position: pos + 1,
        isTopMatch: pos < 3
      });
    });
  }

  function escHtml(str) {
    var div = document.createElement('div');
    div.textContent = str || '';
    return div.innerHTML;
  }

  /* ── Boot ────────────────────────────────────── */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
